import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft } from 'lucide-react';
import { getNotices, getStats, retract } from '@/lib/api';
import {
  explainErrorCode,
  formatFull,
  formatNumber,
  formatRelativeDay,
} from '@/lib/format';
import type { Session, Stats } from '@/lib/types';
import { LINK_ENABLED } from '@/lib/features';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Loading, PageTitle, Stat, StateBadge } from '@/components/shell';

/**
 * Il singolo avviso: cos'è stato mandato, com'è andato, e le due uscite.
 *
 * Le due uscite non sono la stessa cosa e la schermata non le confonde:
 *   - **revoca** = un avviso nuovo che dice «cessato» e che notifica. È la
 *     risposta giusta quando i cittadini stanno agendo su un'informazione che
 *     non vale più.
 *   - **ritiro** = l'avviso mandato per errore, che deve solo sparire. Nessuna
 *     push, nessuna quota restituita.
 */
export function Dettaglio({ session }: { session: Session }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const notices = useQuery({
    queryKey: ['notices', session.istat],
    queryFn: () => getNotices(session.istat, 200),
  });

  const stats = useQuery({
    queryKey: ['stats', session.istat, id],
    queryFn: () => getStats(session.istat, id),
    enabled: Boolean(id),
  });

  const ritira = useMutation({
    mutationFn: () => retract(session.istat, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notices', session.istat] });
      navigate('/storico');
    },
  });

  if (notices.isLoading) return <Loading what="Carico l’avviso" />;

  const notice = (notices.data ?? []).find((n) => n.id === id);
  if (!notice) {
    return (
      <Alert variant="destructive">
        <AlertTriangle />
        <AlertTitle>Avviso non trovato</AlertTitle>
      </Alert>
    );
  }

  const isLive = notice.stato === 'attivo';

  return (
    <div className="space-y-6">
      <PageTitle
        above={
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ChevronLeft />
              Indietro
            </Button>
            <StateBadge state={notice.stato} />
          </div>
        }
      >
        {notice.title}
      </PageTitle>

      <Card>
        <CardContent>
          <p className="whitespace-pre-line text-[15px] leading-relaxed text-ink2">
            {notice.body}
          </p>
          {LINK_ENABLED && notice.link_url && (
            <p className="mt-4 break-all text-[14px] text-ink3">
              {notice.link_label}: {notice.link_url}
            </p>
          )}
        </CardContent>
        <CardFooter className="tnum gap-8 border-t pt-4 text-[13px] text-ink3">
          <span>Pubblicato {formatFull(notice.published_at)}</span>
          <span>Scade {formatFull(notice.expires_at)}</span>
          <span>{notice.actor?.replace(/^console:/, '') || '—'}</span>
        </CardFooter>
      </Card>

      <StatsCard data={stats.data} loading={stats.isLoading} />

      {isLive && (
        <Card className="border-rossa/40">
          <CardHeader>
            <CardTitle>Chiudere questo avviso</CardTitle>
            <CardDescription>
              <strong className="text-ink2">Comunica il cessato</strong> manda
              una notifica che dice che è finita: serve se i cittadini stanno
              ancora agendo su questo avviso.{' '}
              <strong className="text-ink2">Ritira</strong> lo fa solo sparire
              dall’app, senza notificare. In entrambi i casi resta agli atti.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button onClick={() => navigate(`/nuovo?revoca=${notice.id}`)}>
              Comunica il cessato
            </Button>
            <Button
              variant="destructive"
              disabled={ritira.isPending}
              onClick={() => ritira.mutate()}
            >
              {ritira.isPending ? 'Ritiro…' : 'Ritira senza avvisare'}
            </Button>
          </CardContent>
          {ritira.isError && (
            <CardFooter>
              <Alert variant="destructive">
                <AlertTriangle />
                <AlertDescription>
                  Non sono riuscito a ritirarlo: è ancora visibile in app.
                </AlertDescription>
              </Alert>
            </CardFooter>
          )}
        </Card>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Le statistiche, col loro limite scritto sotto.
 *
 * `notifications_log` registra l'esito dell'INVIO, non la consegna al telefono
 * né l'apertura: FCM che accetta un token vuol dire «preso in carico». La
 * frase in calce non è cautela legale, è la ragione per cui un Comune non
 * chiederà «perché solo il 60%?» guardando un numero che non significa quello.
 */
function StatsCard({ data, loading }: { data: Stats | undefined; loading: boolean }) {
  if (loading) return <Loading what="Carico le statistiche" />;
  if (!data) return null;

  if (!data.available) {
    return (
      <Card>
        <CardContent className="text-[15px] text-ink3">
          Questo avviso non ha inviato notifiche: pubblicato in app senza
          svegliare nessuno.
        </CardContent>
      </Card>
    );
  }

  const errors = data.errors ?? [];

  // Una quota di invii falliti c'è sempre: sono app disinstallate e notifiche
  // spente, e su migliaia di destinatari sono decine. Il rosso scatta solo
  // oltre un quinto, dove smette di essere fisiologico e diventa un guasto —
  // un colore d'allarme che si accende ogni volta è un colore che si impara a
  // ignorare, e quando servirà davvero non lo vedrà nessuno.
  const total = (data.sent ?? 0) + (data.failed ?? 0);
  const failedIsAnomaly = total > 0 && (data.failed ?? 0) / total > 0.2;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Consegna</CardTitle>
        {data.first_at && (
          <CardDescription className="tnum">
            Partita {formatRelativeDay(data.first_at)}
          </CardDescription>
        )}
        <CardAction>
          <span className="text-[13px] text-ink4">numeri di invio</span>
        </CardAction>
      </CardHeader>

      <CardContent className="grid gap-6 sm:grid-cols-3">
        {/* `devices` è il conteggio buono; `people` è il suo alias storico e
            resta come ripiego per una risposta da un server non ancora
            aggiornato — leggere solo `devices` mostrerebbe zero. */}
        <Stat
          value={formatNumber(data.devices ?? data.people ?? 0)}
          label="dispositivi raggiunti"
        />
        <Stat value={formatNumber(data.sent ?? 0)} label="invii riusciti" />
        <Stat
          value={formatNumber(data.failed ?? 0)}
          label="invii non riusciti"
          tone={failedIsAnomaly ? 'danger' : 'default'}
        />
      </CardContent>

      {/* Il numero grezzo non dice niente; la causa cambia cosa pensarne —
          mille «app disinstallata» sono fisiologici, mille «token non valido»
          sono un problema nostro. */}
      {errors.length > 0 && (
        <CardContent className="border-t pt-6">
          <p className="mb-2 text-[13px] font-semibold uppercase tracking-[0.06em] text-ink4">
            Perché non sono arrivati
          </p>
          <ul className="space-y-1 text-[15px] text-ink2">
            {errors.map((e) => (
              <li key={e.code} className="tnum flex justify-between gap-4">
                <span>{explainErrorCode(e.code)}</span>
                <span className="text-ink3">{formatNumber(e.count)}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      )}

      <CardFooter className="border-t pt-4 text-[13px] leading-relaxed text-ink3">
        Sono numeri di invio. Non sappiamo se la notifica è stata mostrata o
        aperta: dipende dalle impostazioni del telefono di chi la riceve.
      </CardFooter>
    </Card>
  );
}
